import { queryOptions, useQuery } from '@tanstack/react-query';
import { ApiError } from '../../../components/ApiLoadingGuard/ApiLoadingGuard';
import { DateTime } from 'luxon';
import { SelfServiceSessionDTO, SelfServiceSessionService } from '../../generated';

export interface SelfServiceSession {
  jobsiteId: string;
  sessionId: string;
  sessionStartedAt: DateTime;
  sessionExpiresAt: DateTime;
  status: SelfServiceSessionDTO['status'];
  extended: number;
}

export const selfServiceSessionKeys = {
  base: () => ['selfServiceSession'] as const,
  read: ({ jobsiteId, sessionId }: SelfServiceSessionOptionProps) =>
    [...selfServiceSessionKeys.base(), jobsiteId, sessionId] as const,
};

export interface SelfServiceSessionOptionProps {
  jobsiteId: string;
  sessionId: string;
}

export function selfServiceSessionOptions({ jobsiteId, sessionId }: SelfServiceSessionOptionProps) {
  return queryOptions<SelfServiceSession, ApiError>({
    queryKey: selfServiceSessionKeys.read({ jobsiteId, sessionId }),
    queryFn: async (): Promise<SelfServiceSession> => {
      const response = await SelfServiceSessionService.selfServiceSessionControllerGetSelfServiceSession({
        path: {
          jobsiteId,
          sessionId,
        },
      });

      if (response.data !== undefined) {
        return selfSessionMapping(response.data);
      } else {
        throw (response as any).error || new Error(response?.response?.statusText || 'Unknown error');
      }
    },
    enabled: !!jobsiteId && !!sessionId,
  });
}

export function useSelfServiceSession(props: SelfServiceSessionOptionProps) {
  return useQuery(selfServiceSessionOptions(props));
}

function selfSessionMapping(resp: SelfServiceSessionDTO): SelfServiceSession {
  return {
    jobsiteId: resp.jobsite_id,
    sessionId: resp.id,
    sessionStartedAt: DateTime.fromISO(resp.session_start_at),
    sessionExpiresAt: DateTime.fromISO(resp.session_end_at),
    status: resp.status,
    extended: resp.session_extended,
  };
}
